import React from 'react';
import { useTranslation } from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Paper from '@material-ui/core/Paper'
import Box from '@material-ui/core/Box'
import Avatar from '@material-ui/core/Avatar'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import EcoIcon from '@material-ui/icons/Eco'
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong'

import TotalPlants from './TotalPlants';
import GraniotPremiumButton from "@graniot/common/utils/button";
import { TreeMapLayer } from './TreeMapLayer'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { LayerSelector } from '../LayerSelector'
import UploadTrees from './UploadTrees';
import UploadTreesFarm from './UploadTreesFarm';
import { usePaletteLegend } from '../../../../adapters/paletteRange';
import { usePaletteAdjustmentsEnabled } from '../../../../context/PaletteAdjustmentsContext';

// Empty state shown until this parcel has trees: detect them from imagery, or
// upload positions the grower already has.
const useEmptyStateStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 860,
    margin: '0 auto',
    padding: theme.spacing(1, 0, 2),
  },
  intro: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: theme.spacing(2.5),
  },
  introIcon: {
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(55, 166, 146, 0.12)',
    color: '#2F8C7B',
  },
  introTitle: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: 'rgba(0,0,0,0.85)',
    lineHeight: 1.3,
  },
  introText: {
    fontSize: '0.85rem',
    color: 'rgba(0,0,0,0.55)',
    lineHeight: 1.5,
    marginTop: 3,
    maxWidth: '60ch',
  },
  detect: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: theme.spacing(1.75, 2),
    borderRadius: 12,
    border: '1px solid rgba(0,0,0,0.08)',
    borderLeft: '3px solid #37A692',
    background: 'linear-gradient(180deg, #FAF9F7 0%, #F5F3F0 100%)',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  detectIcon: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    border: '1px solid rgba(0,0,0,0.06)',
    color: '#232C57',
  },
  detectCopy: {
    flexGrow: 1,
  },
  detectTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: 'rgba(0,0,0,0.82)',
    lineHeight: 1.3,
  },
  detectHint: {
    fontSize: '0.8rem',
    color: 'rgba(0,0,0,0.5)',
    marginTop: 2,
  },
  divider: {
    fontSize: '0.8rem',
    color: 'rgba(0,0,0,0.45)',
    margin: theme.spacing(3, 0, 1.5),
  },
}))

const RequestPlants = ({
  farm,
  parcel,
  layers,
  selectedResolution,
  selectedLayer,
  startVegetation,
  maxCC,
  fromDate,
  imagesDate,
  handleRequestTreeAnalysis,
  handleCancelTreeAnalysis,
  mutateSavePlants,
  handleSavePlants,
  featuresPlantsAnalysis,
  zones,
  setZonedGeom,
  setIndexFilter,
  refetchPlants,
  units,
  statisticUnit,
  getCustomLayerScript,
  indexFilter,
  classes,
  setStatusSnackbar,
  setMessageSnackbar,
  setOpenSnackbar,
  onUploaded,
}) => {
  const { t } = useTranslation()
  const emptyState = useEmptyStateStyles()

  const [totalPlants, setTotalPlants] = React.useState(null)
  const [featureData, setFeatureData] = React.useState(null)

  // Legend follows the user's saved palette range for this farm/layer, like the image
  const paletteEnabled = usePaletteAdjustmentsEnabled()
  const { legend } = usePaletteLegend(parcel?.id, selectedLayer, { enabled: paletteEnabled })

  const handleSelectedLayer = (event, newValue) => {
    setSelectedLayer(newValue)
  }

  const disableNotAvalibleSentinelImages = (date) => {
    return indexOfDate(date) === -1
  }

  return (
    <>
      {!featuresPlantsAnalysis ? (
        <Grid item xs={12} sm={12}>
          <div className={emptyState.root}>
            <div className={emptyState.intro}>
              <div className={emptyState.introIcon}>
                <EcoIcon />
              </div>
              <div>
                <Typography className={emptyState.introTitle}>
                  {t('plants_upload.title')}
                </Typography>
                <Typography className={emptyState.introText}>
                  {t(
                    'plants_upload.subtitle'
                  )}
                </Typography>
              </div>
            </div>

            <div className={emptyState.detect}>
              <div className={emptyState.detectIcon}>
                <CenterFocusStrongIcon />
              </div>
              <div className={emptyState.detectCopy}>
                <Typography className={emptyState.detectTitle}>
                  {t('plants_upload.detect_title')}
                </Typography>
                <Typography className={emptyState.detectHint}>
                  {t('plants_upload.detect_hint')}
                </Typography>
              </div>
              <GraniotPremiumButton
                id="request_tree_analysis"
                variant="contained"
                color="primary"
                style={{ textTransform: 'none', borderRadius: 10, fontWeight: 600 }}
                action={handleRequestTreeAnalysis}
                label="Request_Tree_analysis"
              />
            </div>

            <Typography className={emptyState.divider}>
              {t('plants_upload.divider')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <UploadTrees
                  parcel={parcel}
                  refetchPlants={refetchPlants}
                  onUploaded={onUploaded}
                  setStatusSnackbar={setStatusSnackbar}
                  setMessageSnackbar={setMessageSnackbar}
                  setOpenSnackbar={setOpenSnackbar}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <UploadTreesFarm
                  farm={farm}
                  refetchPlants={refetchPlants}
                  onUploaded={onUploaded}
                  setStatusSnackbar={setStatusSnackbar}
                  setMessageSnackbar={setMessageSnackbar}
                  setOpenSnackbar={setOpenSnackbar}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>) : (
        <>
          <Grid item xs={12} sm={5}>
            <Grid
              container
              direction="row"
              style={{ marginBottom: 20 }}
              spacing={3}
            >
              <Grid item xs={12}>
                <TotalPlants totalPlants={totalPlants} />
              </Grid>
              <Grid item xs={12}>
                <LayerSelector
                  layers={layers.filter(layer => 
                    !layer.name.endsWith('WMS') && 
                    !layer.name.startsWith('NATURA_COLOR') && 
                    !layer.name.startsWith('NATURAL_COLOUR') && 
                    !layer.name.startsWith('NATURA-COLOR') && 
                    !layer.name.startsWith('NATURAL-COLOUR')
                  )}
                  selectedLayer={selectedLayer}
                  handleSelectedLayer={handleSelectedLayer}
                  width={'100%'}
                />
              </Grid>
              <Grid item xs={12}>
                {startVegetation && (
                  <KeyboardDatePicker
                    name="base_date"
                    label={t('Analysis date')}
                    value={startVegetation.date}
                    onChange={(date) =>
                      handleChangeStartVegetation(
                        format(date, 'dd/MM/yyyy')
                      )
                    }
                    shouldDisableDate={(date) =>
                      disableNotAvalibleSentinelImages(
                        format(date, 'dd/MM/yyyy')
                      )
                    }
                    maxDate={new Date()}
                    minDate={new Date(fromDate)}
                    format="dd/MM/yyyy"
                    variant="inline"
                    size="small"
                    fullWidth
                    autoOk
                  />
                )}
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleSavePlants}
                      disabled={mutateSavePlants.isLoading}
                      fullWidth
                    >
                      {t('Save_Results')}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleCancelTreeAnalysis}
                      disabled={mutateSavePlants.isLoading}
                      fullWidth
                    >
                      {t('Note.Boton_Cancelar')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={7}>
            <Paper>
              <Box display="flex">
                <Box flexGrow={1}>
                  <TreeMapLayer
                    parcel={parcel}
                    imagesDate={imagesDate}
                    layer={selectedLayer}
                    selectedResolution={selectedResolution}
                    zones={zones}
                    setZonedGeom={setZonedGeom}
                    maxCC={maxCC}
                    startVegetation={startVegetation}
                    setTotalPlants={setTotalPlants}
                    data={featuresPlantsAnalysis}
                    refetch={refetchPlants}
                    statisticUnit={statisticUnit}
                    setFeatureData={setFeatureData}
                    getCustomLayerScript={
                      getCustomLayerScript
                    }
                    indexFilter={indexFilter}
                  />
                </Box>
                <Box>
                  {legend
                    ?.slice(0)
                    .reverse()
                    .map((l, index) => (
                      <Grid key={index} item xs={12}>
                        <Avatar
                          variant="square"
                          className={classes.small}
                          style={{
                            backgroundColor:
                              l.color,
                            fontSize: 12,
                            height:
                              500 /
                              legend?.slice(
                                0
                              ).length,
                          }}
                        >
                          {l.label}
                        </Avatar>
                      </Grid>
                    ))}
                </Box>
              </Box>
            </Paper>
          </Grid>
        </>
      )}
    </>
  )
}

export default RequestPlants;
